// ═══════════════════════════════════════════════════════════════════════════════
// Receipt image display: authed API → blob URL (hides GCS); optional GCS fetch → blob
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import {
  buildPairingScopedImageApiPath,
  isGcsCanonicalHttpsUrl,
  isPairingScopedImageApiPath,
} from '../../lib/pairingImageStream';

const getStoredToken = (): string | null => localStorage.getItem('accessToken');

/**
 * GET authenticated API path with Bearer token; result is blob: in the DOM (hides GCS).
 */
export const useAuthedApiImageBlobUrl = (
  apiPath: string | undefined
): { objectUrl: string | null; loading: boolean; error: boolean } => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(apiPath && apiPath.trim() !== ''));
  const [error, setError] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    const revoke = () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };

    if (!apiPath || apiPath.trim() === '') {
      revoke();
      setObjectUrl(null);
      setLoading(false);
      setError(false);
      return undefined;
    }

    const ac = new AbortController();
    setLoading(true);
    setError(false);
    revoke();
    setObjectUrl(null);

    void (async () => {
      try {
        const token = getStoredToken();
        const res = await fetch(apiPath, {
          signal: ac.signal,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        if (ac.signal.aborted) return;
        revoke();
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setObjectUrl(u);
      } catch {
        if (!ac.signal.aborted) {
          setError(true);
          setObjectUrl(null);
        }
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      ac.abort();
      revoke();
    };
  }, [apiPath]);

  return { objectUrl, loading, error };
};

/** GET /api/receipts/:id/image */
export const useAuthedReceiptImageBlobUrl = (
  receiptId: string | undefined
): { objectUrl: string | null; loading: boolean; error: boolean } =>
  useAuthedApiImageBlobUrl(
    receiptId ? `/api/receipts/${encodeURIComponent(receiptId)}/image` : undefined
  );

const isLikelyGcsHttps = (src: string): boolean =>
  src.startsWith('https://storage.googleapis.com/') ||
  /^https:\/\/[^/]+\.storage\.googleapis\.com\//.test(src);

/**
 * For AI preview URLs pointing at GCS: try CORS fetch → blob so img src is blob:.
 * Falls back to original URL if fetch fails (e.g. bucket CORS not set).
 */
export const useGcsPreviewBlobUrl = (
  src: string | undefined
): { displaySrc: string | null; loading: boolean } => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    const revoke = () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };

    if (!src || src.trim() === '') {
      revoke();
      setBlobUrl(null);
      setLoading(false);
      return undefined;
    }

    if (src.startsWith('blob:') || src.startsWith('data:')) {
      revoke();
      setBlobUrl(null);
      setLoading(false);
      return undefined;
    }

    if (!isLikelyGcsHttps(src)) {
      revoke();
      setBlobUrl(null);
      setLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    setLoading(true);
    revoke();

    void (async () => {
      try {
        const res = await fetch(src, { signal: ac.signal, mode: 'cors' });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        if (ac.signal.aborted) return;
        revoke();
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setBlobUrl(u);
      } catch {
        if (!ac.signal.aborted) {
          revoke();
          setBlobUrl(null);
        }
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      ac.abort();
      revoke();
    };
  }, [src]);

  if (!src || src.trim() === '') {
    return { displaySrc: null, loading: false };
  }

  if (src.startsWith('blob:') || src.startsWith('data:')) {
    return { displaySrc: src, loading: false };
  }

  if (blobUrl) {
    return { displaySrc: blobUrl, loading: false };
  }

  if (isLikelyGcsHttps(src) && loading) {
    return { displaySrc: null, loading: true };
  }

  return { displaySrc: src, loading: false };
};

/**
 * Display GCS canonical URLs via opaque authed stream API; blob/data URLs pass through;
 * legacy signed HTTPS URLs fall back to CORS fetch.
 */
export const usePairingScopedImageBlobUrl = (
  src: string | undefined
): { displaySrc: string | null; loading: boolean } => {
  const trimmed = src?.trim() ?? '';
  const isOpaqueApiPath = isPairingScopedImageApiPath(trimmed);
  const isCanonical = isGcsCanonicalHttpsUrl(trimmed);
  const streamApiPath = isOpaqueApiPath
    ? trimmed
    : isCanonical
      ? (buildPairingScopedImageApiPath(trimmed) ?? undefined)
      : undefined;
  const authed = useAuthedApiImageBlobUrl(streamApiPath);
  const gcsPreview = useGcsPreviewBlobUrl(isOpaqueApiPath || isCanonical ? undefined : src);

  if (trimmed === '') {
    return { displaySrc: null, loading: false };
  }

  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    return { displaySrc: trimmed, loading: false };
  }

  if (isCanonical || isOpaqueApiPath) {
    return { displaySrc: authed.objectUrl, loading: authed.loading };
  }

  return gcsPreview;
};
